import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { stateToHTML } from 'draft-js-export-html';
import { convertFromRaw } from 'draft-js';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { API_BASE } from '../config.js';
import { iconMap } from '../theme/icons';
import { Card, Heading } from '../theme/primitives';
import { stonePalette } from '../theme/brand';

const ErrorIcon = iconMap.error;

const useStyles = makeStyles({
  page: { minHeight: '100vh', backgroundColor: stonePalette[50] },
  centerPage: { minHeight: '100vh', backgroundColor: stonePalette[50], display: 'flex', alignItems: 'center', justifyContent: 'center' },
  centerPagePad: {
    minHeight: '100vh', backgroundColor: stonePalette[50], display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingLeft: tokens.spacingHorizontalXL, paddingRight: tokens.spacingHorizontalXL,
  },
  errorCard: { maxWidth: '24rem', width: '100%', textAlign: 'center' },
  errorIcon: { display: 'block', margin: '0 auto', marginBottom: tokens.spacingVerticalM, fontSize: '36px', color: tokens.colorStatusDangerForeground1 },
  errorText: { color: tokens.colorNeutralForeground2 },
  container: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  title: { marginBottom: tokens.spacingVerticalXXL },
  // Turinys ateina iš CMS (draft-js/HTML), tad žymos stilizuojamos per Griffel
  // vaikinius (descendant) selektorius — tas pats paternas kaip Duk.jsx.
  content: {
    fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.6',
    '& h1': { fontSize: '1.5rem', fontWeight: tokens.fontWeightBold, marginBottom: tokens.spacingVerticalL, color: tokens.colorNeutralForeground1 },
    '& h2': { fontSize: '1.25rem', fontWeight: tokens.fontWeightSemibold, marginTop: tokens.spacingVerticalXXL, marginBottom: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 },
    '& h3': { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXS, color: tokens.colorNeutralForeground1 },
    '& h4': { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalXS },
    '& h6': { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXS, color: tokens.colorNeutralForeground1 },
    '& p': { marginBottom: tokens.spacingVerticalS },
    '& p:last-child': { marginBottom: 0 },
    '& ul': { listStyleType: 'disc', marginLeft: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalS },
    '& ol': { listStyleType: 'decimal', marginLeft: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalS },
    '& li': { marginBottom: tokens.spacingVerticalXXS },
    '& ol > li': { marginTop: tokens.spacingVerticalS },
    '& strong': { fontWeight: tokens.fontWeightSemibold },
    '& a': { color: tokens.colorBrandForegroundLink, textDecorationLine: 'underline', textUnderlineOffset: '2px' },
    '& table': { width: '100%', borderCollapse: 'collapse', marginBottom: tokens.spacingVerticalM },
    '& th': { textAlign: 'left', padding: tokens.spacingVerticalXS, border: `1px solid ${tokens.colorNeutralStroke2}`, backgroundColor: tokens.colorNeutralBackground3, fontWeight: tokens.fontWeightSemibold },
    '& td': { padding: tokens.spacingVerticalXS, border: `1px solid ${tokens.colorNeutralStroke2}` },
  },
});

function Text() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lang = i18n.language?.slice(0, 2) || 'lt';
  const s = useStyles();

  useEffect(() => {
    const url = lang !== 'lt'
      ? `${API_BASE}/textpages/${slug}?lang=${lang}`
      : `${API_BASE}/textpages/${slug}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error('Puslapis nerastas'); return r.json(); })
      .then(setPage)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  if (loading) {
    return (
      <div className={s.centerPage}>
        <Spinner size="medium" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={s.centerPagePad}>
        <Card className={s.errorCard} padding={tokens.spacingHorizontalXL}>
          <ErrorIcon className={s.errorIcon} />
          <p className={s.errorText}>{error}</p>
        </Card>
      </div>
    );
  }

  let contentHtml = '';
  if (page?.content) {
    try {
      const parsed = JSON.parse(page.content);
      if (parsed && parsed.blocks) {
        const contentState = convertFromRaw(parsed);
        contentHtml = stateToHTML(contentState);
      } else {
        contentHtml = page.content;
      }
    } catch {
      contentHtml = page.content;
    }
  }

  const safeHtml = DOMPurify.sanitize(contentHtml);

  return (
    <div className={s.page}>
      <div className={s.container}>
        <Card padding={tokens.spacingHorizontalXXL}>
          <Heading variant="h1" className={s.title}>{page?.title}</Heading>
          <div
            className={s.content}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </Card>
      </div>
    </div>
  );
}

export default Text;
